import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

/**
 * =====================================================
 * CRITICAL DESIGN PRINCIPLE - READ THIS FIRST
 * =====================================================
 * 
 * runningUser is ALWAYS the current user context for ALL operations.
 * 
 * When using auth context in components:
 *   ✅ Use: runningUser, isCustomer(), isCalendarAdmin(), isSuperAdmin(), getAuthHeaders()
 *   ❌ Don't use: user.role, user.id for logic (unless you specifically need loginUser)
 * 
 * Run As is just an authority check + context switch.
 * Once switched, runningUser IS the effective user - no special "Run As mode" logic needed.
 * 
 * See /app/memory/CONVENTIONS.md for full documentation.
 * =====================================================
 * 
 * Architecture:
 * 
 * loginUser   - Immutable after authentication, the ACTUAL logged-in user
 * runningUser - Current operating context, used for ALL authorization & API calls
 * runAsStack  - LIFO stack of caller IDs when in Run As mode (max 2 levels)
 * 
 * When not in Run As mode:
 *   - runningUser === loginUser
 *   - runAsStack = []
 * 
 * When in Run As mode (e.g., Super Admin → Calendar Admin):
 *   - loginUser = Super Admin
 *   - runningUser = Calendar Admin
 *   - runAsStack = [{ id, role, returnUrl }] (caller info)
 * 
 * When in nested Run As (Super Admin → Calendar Admin → Customer):
 *   - loginUser = Super Admin
 *   - runningUser = Customer
 *   - runAsStack = [
 *       { id: SuperAdminId, role: 'super_admin', returnUrl: '/admin/users' },
 *       { id: CalendarAdminId, role: 'calendar_admin', returnUrl: '/admin/users' }
 *     ]
 */

// NOTE: We no longer use a global axios interceptor.
// Instead, use getAuthHeaders() for requests that need Run As context,
// or just { Authorization: `Bearer ${token}` } for admin-only requests.

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  
  // Login User - The actual authenticated user (immutable after login)
  const [loginUser, setLoginUser] = useState(null);
  
  // Running User - Current operating context (changes with Run As)
  const [runningUser, setRunningUser] = useState(null);
  
  // Run As Stack - LIFO stack of caller info when in Run As mode
  // Each entry: { id, role, fullName, email, returnUrl }
  const [runAsStack, setRunAsStack] = useState(() => {
    const stored = localStorage.getItem('runAsStack');
    return stored ? JSON.parse(stored) : [];
  });

  // Persist runAsStack to localStorage
  useEffect(() => {
    if (runAsStack.length > 0) {
      localStorage.setItem('runAsStack', JSON.stringify(runAsStack));
    } else {
      localStorage.removeItem('runAsStack');
    }
  }, [runAsStack]);

  // Create auth headers - Unified architecture
  // ALWAYS sends X-Run-As-User-Id header with runningUser.id
  // - Direct mode: runningUser.id === loginUser.id (same user, backend fast-paths)
  // - Run As mode: runningUser.id !== loginUser.id (impersonating, backend validates)
  // This unified approach eliminates conditional logic and prevents auth bugs
  const getAuthHeaders = useCallback(() => {
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      // Always include X-Run-As-User-Id when we have a runningUser
      // In direct mode, this equals loginUser.id; in Run As mode, it's the target user
      if (runningUser?.id) {
        headers['X-Run-As-User-Id'] = runningUser.id;
      }
    }
    return headers;
  }, [token, runningUser]);

  // Fetch login user on mount/token change
  useEffect(() => {
    if (token) {
      fetchLoginUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Restore runningUser from stack on page reload
  useEffect(() => {
    if (loginUser && runAsStack.length > 0) {
      // We have a stack, need to restore runningUser
      const storedRunningUser = localStorage.getItem('runningUser');
      if (storedRunningUser) {
        setRunningUser(JSON.parse(storedRunningUser));
      }
    }
  }, [loginUser]);

  const fetchLoginUser = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = response.data;
      setLoginUser(userData);
      
      // If not in Run As mode, runningUser = loginUser
      const storedRunningUser = localStorage.getItem('runningUser');
      const storedStack = localStorage.getItem('runAsStack');
      
      if (storedRunningUser && storedStack) {
        // Restore Run As state
        setRunningUser(JSON.parse(storedRunningUser));
        setRunAsStack(JSON.parse(storedStack));
      } else {
        // Not in Run As mode
        setRunningUser(userData);
      }
      
      return userData;
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Refresh user data (e.g., after plan upgrade)
  const refreshUser = async () => {
    if (!token) return null;
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = response.data;
      setLoginUser(userData);
      
      // If not in Run As mode, also update runningUser
      if (runAsStack.length === 0) {
        setRunningUser(userData);
      }
      return userData;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  };

  const register = async (email, password, fullName, phone, acceptsSms = false) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/register`, {
        email,
        password,
        full_name: fullName,
        phone: phone,
        accepts_sms: acceptsSms,
        role: 'calendar_admin'
      });
      
      if (response.data.requires_verification) {
        return { 
          success: true, 
          requiresVerification: true,
          email: response.data.email,
          message: response.data.message
        };
      }
      
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setLoginUser(userData);
      setRunningUser(userData);
      localStorage.setItem('token', access_token);
      return { success: true, requiresVerification: false };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, null, {
        params: { email, password }
      });
      
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setLoginUser(userData);
      setRunningUser(userData);
      localStorage.setItem('token', access_token);
      
      // Clear any Run As state on fresh login
      clearRunAsState();
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const logout = (redirectUrl = null) => {
    const currentUser = runningUser;
    const lastCalendarSymbolicName = localStorage.getItem('lastCalendarSymbolicName');
    
    if (currentUser?.role === 'customer') {
      localStorage.setItem('lastUserRole', 'customer');
    } else {
      localStorage.removeItem('lastUserRole');
    }
    
    setLoginUser(null);
    setRunningUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    clearRunAsState();
    
    if (redirectUrl) return redirectUrl;
    
    if (currentUser?.role === 'customer' && lastCalendarSymbolicName) {
      return `/customer/login/${lastCalendarSymbolicName}`;
    }
    
    return '/';
  };

  const updateProfile = async (data) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/auth/me`, data, {
        headers: getAuthHeaders()
      });
      
      // Update the appropriate user based on context
      if (runAsStack.length === 0) {
        setLoginUser(response.data);
        setRunningUser(response.data);
      } else {
        setRunningUser(response.data);
      }
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Update failed'
      };
    }
  };

  // Clear all Run As state
  const clearRunAsState = () => {
    setRunAsStack([]);
    localStorage.removeItem('runAsStack');
    localStorage.removeItem('runningUser');
  };

  /**
   * Check if loginUser can run as targetUser
   * Rules:
   *   - Super Admin can run as Calendar Admin or Customer
   *   - Calendar Admin can run as Customer (their own customers only - enforced by backend)
   *   - Maximum 2 levels of nesting
   */
  const canRunAs = (fromUser, targetUser) => {
    if (!fromUser || !targetUser) return false;
    
    const fromRole = fromUser.role;
    const targetRole = targetUser.role;
    
    // Super Admin can run as Calendar Admin or Customer
    if (fromRole === 'super_admin') {
      return targetRole === 'calendar_admin' || targetRole === 'customer';
    }
    
    // Calendar Admin can run as Customer
    if (fromRole === 'calendar_admin') {
      return targetRole === 'customer';
    }
    
    return false;
  };

  /**
   * Start Run As mode
   * @param {Object} targetUser - The user to run as
   * @param {string} returnUrl - URL to return to when stopping Run As
   * @returns {boolean} - Success status
   */
  const startRunAs = async (targetUser, returnUrl = null) => {
    // Validate: current runningUser must be able to run as targetUser
    if (!canRunAs(runningUser, targetUser)) {
      console.error('You do not have permission to run as this user');
      return false;
    }
    
    // Check max nesting level (2 levels max)
    if (runAsStack.length >= 2) {
      console.error('Maximum Run As nesting level reached (2)');
      return false;
    }
    
    // Push current runningUser info to stack
    const callerInfo = {
      id: runningUser.id,
      role: runningUser.role,
      fullName: runningUser.full_name,
      email: runningUser.email,
      returnUrl: returnUrl || window.location.pathname
    };
    
    const newStack = [...runAsStack, callerInfo];
    setRunAsStack(newStack);
    
    // Switch to target user - include plan_features for feature-based access control
    const newRunningUser = {
      id: targetUser.id,
      role: targetUser.role,
      full_name: targetUser.full_name,
      email: targetUser.email,
      phone: targetUser.phone,
      plan_id: targetUser.plan_id,
      plan_type: targetUser.plan_type,
      plan_features: targetUser.plan_features,
      stripe_customer_id: targetUser.stripe_customer_id,
      subscription_status: targetUser.subscription_status
    };
    
    setRunningUser(newRunningUser);
    localStorage.setItem('runningUser', JSON.stringify(newRunningUser));
    
    return true;
  };

  /**
   * Stop Run As mode - returns to previous level
   * @returns {string} - URL to navigate to
   */
  const stopRunAs = () => {
    if (runAsStack.length === 0) {
      // Not in Run As mode
      return '/dashboard';
    }
    
    // Pop the last caller from stack
    const newStack = [...runAsStack];
    const previousCaller = newStack.pop();
    
    setRunAsStack(newStack);
    
    if (newStack.length === 0) {
      // Returning to loginUser
      setRunningUser(loginUser);
      localStorage.removeItem('runningUser');
    } else {
      // Returning to previous level (not loginUser)
      // Need to fetch the previous user's full data
      // For now, reconstruct from caller info
      const previousUser = {
        id: previousCaller.id,
        role: previousCaller.role,
        full_name: previousCaller.fullName,
        email: previousCaller.email
      };
      setRunningUser(previousUser);
      localStorage.setItem('runningUser', JSON.stringify(previousUser));
    }
    
    return previousCaller.returnUrl || '/dashboard';
  };

  // Computed properties - ALL use runningUser for authorization
  const isRunAsMode = () => runAsStack.length > 0;
  
  const getRunAsLevel = () => runAsStack.length;
  
  const getRunAsReturnUrl = () => {
    if (runAsStack.length === 0) return '/dashboard';
    return runAsStack[runAsStack.length - 1].returnUrl || '/dashboard';
  };

  // Role checks - ALL based on runningUser
  const isSuperAdmin = () => runningUser?.role === 'super_admin';
  const isCalendarAdmin = () => runningUser?.role === 'calendar_admin';
  const isCustomer = () => runningUser?.role === 'customer' || runningUser?.role === 'non_registered_customer';
  const isAdmin = () => runningUser?.role === 'super_admin' || runningUser?.role === 'calendar_admin';

  // Get effective role (runningUser's role)
  const getEffectiveRole = () => runningUser?.role;
  
  // Get login user's role (for UI elements like "Stop Run As" visibility)
  const getLoginUserRole = () => loginUser?.role;

  // For backward compatibility
  const user = runningUser;

  const value = {
    // Users
    user,               // Alias for runningUser (backward compatibility)
    loginUser,          // The actual logged-in user
    runningUser,        // Current operating context
    
    // Auth state
    token,
    loading,
    isAuthenticated: !!loginUser,
    
    // Auth actions
    register,
    login,
    logout,
    updateProfile,
    refreshUser,
    setToken,
    setUser: setRunningUser,
    setLoginUser,  // CUSTOMER LOGIN FIX (Jan 15, 2026): Export setLoginUser for customer login
    
    // Role checks (all use runningUser)
    isSuperAdmin,
    isCalendarAdmin,
    isAdmin,
    isCustomer,
    getEffectiveRole,
    getLoginUserRole,
    
    // Run As
    runAsStack,
    isRunAsMode,
    getRunAsLevel,
    canRunAs,
    startRunAs,
    stopRunAs,
    getRunAsReturnUrl,
    getAuthHeaders
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
